/**
 * EngagementsParametres — Config entreprise + destinataires alertes.
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardHeader, CardContent, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Save, Loader2, Building2, Mail, Plus, X, Bell, Send } from "lucide-react";
import { toast } from "sonner";

export default function EngagementsParametres() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [recapEmail, setRecapEmail] = useState("");

  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/engagements/settings"); setS(r.data); }
      catch (e) { toast.error(apiError(e)); }
    })();
  }, []);

  const upd = (k, v) => setS(prev => ({ ...prev, [k]: v }));

  const addEmail = () => {
    if (!newEmail || !newEmail.includes("@")) return;
    upd("emails_destinataires", [...(s.emails_destinataires || []), newEmail]);
    setNewEmail("");
  };
  const removeEmail = (e) => upd("emails_destinataires", (s.emails_destinataires || []).filter(x => x !== e));

  const addRecapEmail = () => {
    if (!recapEmail || !recapEmail.includes("@")) return;
    upd("recap_mensuel_destinataires", [...(s.recap_mensuel_destinataires || []), recapEmail]);
    setRecapEmail("");
  };
  const removeRecapEmail = (e) => upd("recap_mensuel_destinataires", (s.recap_mensuel_destinataires || []).filter(x => x !== e));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/engagements/settings", s);
      toast.success("Paramètres sauvegardés");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const r = await api.post("/engagements/alertes/preview");
      setPreview(r.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setPreviewing(false); }
  };

  const sendNow = async () => {
    setSending(true);
    try {
      const r = await api.post("/engagements/alertes/send-now");
      toast.success(`${r.data.sent} email(s) envoyé(s) · ${r.data.alerts || 0} alerte(s)`);
      setPreview(null);
    } catch (e) { toast.error(apiError(e)); }
    finally { setSending(false); }
  };

  if (!s) return <div className="p-6 text-slate-500">Chargement...</div>;

  return (
    <div className="space-y-6 max-w-3xl" data-testid="engagements-parametres-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Paramètres — Engagements</h1>
        <p className="text-sm text-slate-500">Coordonnées utilisées dans les lettres de résiliation générées par IA</p>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="w-4 h-4" />Entreprise (expéditeur)</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><Label>Nom de l'entreprise</Label><Input value={s.entreprise_nom || ""} onChange={e => upd("entreprise_nom", e.target.value)} data-testid="nom-input" /></div>
          <div><Label>Forme juridique</Label><Input value={s.entreprise_forme_juridique || ""} onChange={e => upd("entreprise_forme_juridique", e.target.value)} placeholder="SARL, SAS…" /></div>
          <div className="md:col-span-2"><Label>Adresse du siège social</Label><Textarea rows={2} value={s.entreprise_adresse || ""} onChange={e => upd("entreprise_adresse", e.target.value)} /></div>
          <div><Label>SIRET</Label><Input value={s.entreprise_siret || ""} onChange={e => upd("entreprise_siret", e.target.value)} /></div>
          <div><Label>Représentant légal</Label><Input value={s.representant_legal || ""} onChange={e => upd("representant_legal", e.target.value)} placeholder="Prénom Nom · Titre" /></div>
          <div className="md:col-span-2"><Label>Email expéditeur</Label><Input type="email" value={s.email_expediteur || ""} onChange={e => upd("email_expediteur", e.target.value)} /></div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" />Alertes préavis</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Alertes activées</p>
              <p className="text-xs text-slate-500">Envoi automatique aux jalons J-90, J-60, J-30 et J-7 · CRON toutes les 6h</p>
            </div>
            <Switch checked={s.alertes_actives ?? true} onCheckedChange={v => upd("alertes_actives", v)} data-testid="alertes-switch" />
          </div>
          <div>
            <Label>Destinataires alertes préavis</Label>
            <div className="flex gap-2 mt-1">
              <Input type="email" placeholder="email@entreprise.fr" value={newEmail} onChange={e => setNewEmail(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addEmail(); } }} data-testid="new-email-input" />
              <Button variant="outline" onClick={addEmail}><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {(s.emails_destinataires || []).map(e => (
                <Badge key={e} variant="outline" className="pl-3 pr-1 py-1">
                  {e}
                  <button onClick={() => removeEmail(e)} className="ml-1 hover:bg-slate-200 rounded p-0.5"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
              {(!s.emails_destinataires || s.emails_destinataires.length === 0) && <p className="text-xs text-slate-400">Aucun destinataire configuré</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Mail className="w-4 h-4" />Récap mensuel</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium">Récap mensuel activé</p>
              <p className="text-xs text-slate-500">Le 1er de chaque mois : résiliations en cours + préavis à 60j</p>
            </div>
            <Switch checked={s.recap_mensuel_actif ?? true} onCheckedChange={v => upd("recap_mensuel_actif", v)} data-testid="recap-switch" />
          </div>
          <div>
            <Label>Destinataires récap mensuel</Label>
            <p className="text-[10px] text-slate-400 mb-1">Vide = utilise la liste alertes préavis ci-dessus</p>
            <div className="flex gap-2 mt-1">
              <Input type="email" placeholder="cfo@entreprise.fr" value={recapEmail} onChange={e => setRecapEmail(e.target.value)}
                          onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addRecapEmail(); } }} data-testid="recap-email-input" />
              <Button variant="outline" onClick={addRecapEmail}><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2 mt-3">
              {(s.recap_mensuel_destinataires || []).map(e => (
                <Badge key={e} variant="outline" className="pl-3 pr-1 py-1">
                  {e}
                  <button onClick={() => removeRecapEmail(e)} className="ml-1 hover:bg-slate-200 rounded p-0.5"><X className="w-3 h-3" /></button>
                </Badge>
              ))}
              {(!s.recap_mensuel_destinataires || s.recap_mensuel_destinataires.length === 0) && <p className="text-xs text-slate-400">Aucun destinataire spécifique — récap envoyé aux destinataires alertes</p>}
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="flex justify-between items-center gap-2">
        <div className="flex gap-2">
          <Button variant="outline" onClick={runPreview} disabled={previewing} data-testid="preview-alerts-btn">
            {previewing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Bell className="w-4 h-4 mr-2" />}Prévisualiser alertes
          </Button>
          <Button variant="outline" onClick={sendNow} disabled={sending} data-testid="send-alerts-btn">
            {sending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}Envoyer maintenant
          </Button>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Enregistrer
        </Button>
      </div>

      {preview && (
        <Card className="border-blue-200 bg-blue-50/40" data-testid="alerts-preview">
          <CardHeader><CardTitle className="text-sm flex items-center gap-2"><Bell className="w-4 h-4 text-blue-600" />Aperçu des alertes</CardTitle></CardHeader>
          <CardContent className="space-y-3 text-sm">
            <div className="flex flex-wrap gap-4">
              <div><span className="text-slate-500">Alertes actives :</span> <strong>{preview.alertes_actives ? "Oui" : "Non"}</strong></div>
              <div><span className="text-slate-500">Destinataires :</span> <strong>{preview.recipients_count}</strong></div>
              <div><span className="text-slate-500">Nouvelles alertes :</span> <strong className="text-blue-700">{preview.new_count}</strong></div>
            </div>
            {preview.alerts?.length === 0 ? (
              <p className="text-slate-500 italic">Aucune alerte à ce jour (aucun engagement n'atteint J-90/J-60/J-30/J-7).</p>
            ) : (
              <ul className="space-y-1.5">
                {preview.alerts.map((a, i) => (
                  <li key={i} className={`flex items-center justify-between text-xs p-2 rounded ${a.already_sent ? "bg-slate-100 text-slate-500 line-through" : "bg-white"}`}>
                    <span><strong>{a.prestataire}</strong> — {a.nom_contrat}</span>
                    <Badge className={a.jalon <= 7 ? "bg-red-100 text-red-700" : a.jalon <= 30 ? "bg-orange-100 text-orange-700" : a.jalon <= 60 ? "bg-yellow-100 text-yellow-700" : "bg-blue-100 text-blue-700"}>
                      J-{a.jalon} · {a.days_remaining}j
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
